/**
 * OTP Helper Functions
 * Utility functions for OTP handling and validation
 */

/**
 * Normalize phone number to include country code
 * @param {string} phone - Phone number (with or without country code)
 * @returns {string} Normalized phone number with +965 prefix
 */
export const formatPhoneNumber = (phone) => {
    if (!phone) return '';

    // Remove all spaces and special characters except +
    let cleaned = phone.replace(/[^\d+]/g, '');

    // If already has country code, return as is
    if (cleaned.startsWith('+965')) {
        return cleaned;
    }

    // If starts with 965 but no +, add it
    if (cleaned.startsWith('965')) {
        return '+' + cleaned;
    }

    // If starts with 0, remove it and add +965
    if (cleaned.startsWith('0')) {
        return '+965' + cleaned.substring(1);
    }

    // Otherwise, add +965 prefix
    return '+965' + cleaned;
};

/**
 * Validate OTP code format
 * @param {string} code - OTP code to validate
 * @returns {boolean} True if valid 6-digit code
 */
export const validateOtp = (code) => {
    if (!code) return false;

    // Remove any spaces or special characters
    const cleaned = code.replace(/\D/g, '');

    // Must be exactly 6 digits
    return cleaned.length === 6 && /^\d{6}$/.test(cleaned);
};

/**
 * Calculate remaining time in seconds until expiration
 * @param {string} expiresAt - ISO 8601 datetime string
 * @returns {number} Remaining seconds (0 if expired)
 */
export const calculateRemainingTime = (expiresAt) => {
    if (!expiresAt) return 0;

    const expiryTime = new Date(expiresAt).getTime();
    const currentTime = new Date().getTime();
    const remaining = Math.floor((expiryTime - currentTime) / 1000);

    return remaining > 0 ? remaining : 0;
};

/**
 * Check if identifier is a phone number
 * @param {string} identifier - Phone or email string
 * @returns {boolean} True if appears to be a phone number
 */
export const isPhoneNumber = (identifier) => {
    if (!identifier) return false;

    // Check if starts with + or contains mostly digits
    // Fixed: Removed backslashes before ( and )
    const cleaned = identifier.replace(/[\s\-()]/g, '');

    // Fixed: Removed backslashes before +, (, and ) inside the brackets
    return /^[+\d][\d\s\-()]{7,}$/.test(identifier) ||
        cleaned.startsWith('+') ||
        (cleaned.replace(/\D/g, '').length >= 8 && cleaned.replace(/\D/g, '').length <= 15);
};

/**
 * Check if identifier is an email address
 * @param {string} identifier - Phone or email string
 * @returns {boolean} True if appears to be an email
 */
export const isEmail = (identifier) => {
    if (!identifier) return false;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(identifier);
};

/**
 * Format time in MM:SS format
 * @param {number} seconds - Total seconds
 * @returns {string} Formatted time string (e.g., "04:30")
 */
export const formatTime = (seconds) => {
    if (seconds <= 0) return '00:00';

    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

/**
 * Get optimal channel based on identifier
 * @param {string} identifier - Phone or email string
 * @returns {string} 'sms' or 'email'
 */
export const getChannelFromIdentifier = (identifier) => {
    if (isEmail(identifier)) {
        return 'email';
    }
    return 'sms';
};

/**
 * Mask phone number for display (show last 4 digits)
 * @param {string} phone - Phone number
 * @returns {string} Masked phone (e.g., "+965 **** 5678")
 */
export const maskPhoneNumber = (phone) => {
    if (!phone) return '';

    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length < 4) return phone;

    const last4 = cleaned.slice(-4);
    const countryCode = phone.startsWith('+965') ? '+965' : '';

    return `${countryCode} **** ${last4}`;
};

/**
 * Mask email for display (show first char and domain)
 * @param {string} email - Email address
 * @returns {string} Masked email (e.g., "a****@example.com")
 */
export const maskEmail = (email) => {
    if (!email || !email.includes('@')) return email;

    const [local, domain] = email.split('@');
    if (local.length <= 1) return email;

    return `${local[0]}****@${domain}`;
};
